import { useState, useEffect, useRef, useContext } from 'react';

import resourcesService from '../services/resourcesService';
import { formatUnixTimestampTime } from '../utils/dateUtils';
import { AuthContext } from '../contexts/AuthContext';

import ResourceFileUploadModal from '../components/ResourceFileUploadModal';
import CategoryManagementModal from '../components/CategoryManagementModal';
import FilePreviewModal from '../components/FilePreviewModal';
import MarkdownRenderer from '../components/MarkdownRenderer';
import './Resources.css';

function CollapsibleSources({ sources, categoryId }) {
  const [collapsed, setCollapsed] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFileUri, setModalFileUri] = useState(null);
  const [modalFilename, setModalFilename] = useState('');

  // Remove duplicate sources by stringifying a unique key for each source
  const seen = new Set();
  const uniqueSources = sources.filter((src) => {
    let key;
    if (src.generatedResponsePart && Array.isArray(src.retrievedReferences)) {
      key = JSON.stringify({
        generatedResponsePart: src.generatedResponsePart,
        retrievedReferences: src.retrievedReferences.map(ref => {
          return {
            uri: ref.metadata?.['x-amz-bedrock-kb-source-uri'] || ref.location?.s3Location?.uri || null
          };
        })
      });
    } else {
      key = src.url || src.title || JSON.stringify(src);
    }
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Helper to deduplicate references by s3uri or text
  function dedupeReferences(references) {
    const refSeen = new Set();
    return references.filter(ref => {
      const s3uri = ref.metadata?.['x-amz-bedrock-kb-source-uri'] || ref.location?.s3Location?.uri || null;
      const text = ref.content?.text || null;
      const refKey = s3uri || text || JSON.stringify(ref);
      if (refSeen.has(refKey)) return false;
      refSeen.add(refKey);
      return true;
    });
  }

  // Helper to extract filename from s3 uri
  function getFilenameFromUri(uri) {
    if (!uri) return '';
    const parts = uri.split('/');
    return parts[parts.length - 1];
  }

  // Fetch file content using resourcesService
  async function fetchFileContent(fileUri) {
    const filename = getFilenameFromUri(fileUri);
    
    // Check if it's a DOCX file and convert to PDF for preview
    const isDocx = filename.toLowerCase().endsWith('.docx');
    const convertToPdf = isDocx;
    
    const { blob, headers } = await resourcesService.downloadFile(filename, categoryId, convertToPdf);
    const contentType = headers['content-type'] || '';
    
    console.log('File download result:', { filename, contentType, blobSize: blob.size, blob, convertToPdf });
    
    if (contentType.startsWith('image/')) {
      return URL.createObjectURL(blob);
    } else if (contentType.startsWith('text/') || contentType.includes('json')) {
      // Read blob as text
      return await blob.text();
    } else if (contentType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf') || convertToPdf) {
      // Create blob URL for PDF with explicit type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      console.log('Created PDF blob URL:', url);
      return url;
    } else if (contentType.includes('msword') || contentType.includes('officedocument')) {
      return URL.createObjectURL(blob);
    } else {
      // Fallback: download
      return URL.createObjectURL(blob);
    }
  }

  function handleFileClick(uri) {
    setModalFileUri(uri);
    setModalFilename(getFilenameFromUri(uri));
    setModalOpen(true);
  }

  function handleModalClose() {
    setModalOpen(false);
    setModalFileUri(null);
    setModalFilename('');
  }

  return (
    <div>
      <button
        className="btn btn-sm btn-outline-info mb-2"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
      >
        {collapsed ? 'Show Sources' : 'Hide Sources'}
      </button>
      {!collapsed && (
        <ul className="mb-0 ps-3">
          {uniqueSources.map((src, i) => {
            if (src.generatedResponsePart && Array.isArray(src.retrievedReferences)) {
              let partText = src.generatedResponsePart;
              if (typeof partText === 'object' && partText.textResponsePart && partText.textResponsePart.text) {
                partText = partText.textResponsePart.text;
              } else if (typeof partText === 'object') {
                partText = JSON.stringify(partText);
              }
              const dedupedRefs = dedupeReferences(src.retrievedReferences);
              return (
                <li key={i}>
                  <div>
                    <em>{partText}</em>
                    <ul>
                      {dedupedRefs.map((ref, j) => {
                        let s3uri = null;
                        if (ref.metadata && ref.metadata['x-amz-bedrock-kb-source-uri']) {
                          s3uri = ref.metadata['x-amz-bedrock-kb-source-uri'];
                        } else if (ref.location && ref.location.s3Location && ref.location.s3Location.uri) {
                          s3uri = ref.location.s3Location.uri;
                        }
                        if (s3uri) {
                          const filename = getFilenameFromUri(s3uri);
                          return (
                            <li key={j}>
                              <button
                                type="button"
                                className="btn btn-link p-0"
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                onClick={() => handleFileClick(s3uri)}
                              >
                                {filename}
                              </button>
                            </li>
                          );
                        }
                        // fallback: show text or JSON
                        return (
                          <li key={j}>
                            {ref.content && ref.content.text ? ref.content.text.substring(0, 80) + (ref.content.text.length > 80 ? '...' : '') : JSON.stringify(ref)}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </li>
              );
            }
            // Fallback for old/simple format
            if (src.url) {
              const filename = getFilenameFromUri(src.url);
              return (
                <li key={i}>
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => handleFileClick(src.url)}
                  >
                    {src.title || filename}
                  </button>
                </li>
              );
            }
            return (
              <li key={i}>
                {src.title || JSON.stringify(src)}
              </li>
            );
          })}
        </ul>
      )}
      <FilePreviewModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        fileUri={modalFileUri}
        filename={modalFilename}
        fetchFileContent={fetchFileContent}
      />
    </div>
  );
}

const Resources = () => {
  const { hasPermission } = useContext(AuthContext);
  const isAdmin = hasPermission('admin');
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryManagementModal, setShowCategoryManagementModal] = useState(false);
  const [copyingIndex, setCopyingIndex] = useState(null);
  const [exportingIndex, setExportingIndex] = useState(null);

  // Fetch categories from the resources service API
  const didFetchCategories = useRef(false);
  const fetchCategories = async () => {
    try {
      const categoryArray = await resourcesService.fetchCategories();
      setCategories(categoryArray);
      if (categoryArray.length > 0 && !selectedCategory) {
        setSelectedCategory(categoryArray[0].category_id);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    if (didFetchCategories.current) return;
    didFetchCategories.current = true;
    fetchCategories();
  }, []);

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!selectedCategory || !question.trim()) {
      alert('Please select a category and enter a question');
      return;
    }
    setLoading(true);
    try {
      const queryPayload = {
        categoryId: selectedCategory,
        question: question,
        sessionId: sessionId || undefined
      };
      
      const data = await resourcesService.queryKnowledgeBase(queryPayload);
      
      // Update chat history
      const newEntry = {
        question: question,
        answer: data.answer,
        category_id: selectedCategory,
        sources: data.sources || [],
        timestamp: data.timestamp || null
      };
      
      setChatHistory(prev => [...prev, newEntry]);
      setSessionId(data.session_id);
      setQuestion(''); // Clear the question field
    } catch (error) {
      console.error('Error:', error.message);
      alert('Failed to query knowledge base. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setChatHistory([]);
    setSessionId('');
    setQuestion('');
  };

  const handleCopyResponse = async (entry, index) => {
    setCopyingIndex(index);
    try {
      let textToCopy = `Question: ${entry.question}\n\n`;
      textToCopy += `Answer:\n${entry.answer}\n`;
      
      if (entry.sources && entry.sources.length > 0) {
        textToCopy += '\n\nSources:\n';
        entry.sources.forEach((src, i) => {
          if (src.generatedResponsePart && Array.isArray(src.retrievedReferences)) {
            src.retrievedReferences.forEach(ref => {
              const s3uri = ref.metadata?.['x-amz-bedrock-kb-source-uri'] || ref.location?.s3Location?.uri;
              if (s3uri) {
                const filename = s3uri.split('/').pop();
                textToCopy += `- ${filename}\n`;
              }
            });
          } else if (src.url) {
            const filename = src.url.split('/').pop();
            textToCopy += `- ${src.title || filename}\n`;
          }
        });
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setTimeout(() => setCopyingIndex(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy to clipboard');
      setCopyingIndex(null);
    }
  };

  const handleExportResponse = async (entry, index) => {
    setExportingIndex(index);
    try {
      // Dynamic import of jsPDF
      const { default: jsPDF } = await import('jspdf');
      
      // Create PDF
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 60;
      const maxWidth = pageWidth - (2 * margin);
      let yPosition = margin;
      
      // Helper function to add text with word wrapping and page breaks
      const addText = (text, fontSize, isBold = false, color = [0, 0, 0], lineSpacing = 1.4) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.setTextColor(...color);
        
        const lines = pdf.splitTextToSize(text, maxWidth);
        const lineHeight = fontSize * lineSpacing;
        
        lines.forEach(line => {
          if (yPosition + lineHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
        
        return yPosition;
      };
      
      // Helper function to add spacing
      const addSpacing = (space) => {
        yPosition += space;
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
      };
      
      // Add header with category name
      const categoryName = categories.find(c => c.category_id === entry.category_id)?.category_name || 'Knowledge Base';
      pdf.setFillColor(0, 102, 204);
      pdf.rect(0, 0, pageWidth, 80, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text(categoryName, margin, 50);
      yPosition = 100;
      
      // Add timestamp
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const timestamp = entry.timestamp ? formatUnixTimestampTime(entry.timestamp) : new Date().toLocaleString();
      pdf.text(`Generated: ${timestamp}`, margin, yPosition);
      yPosition += 30;
      
      // Add Question section
      pdf.setTextColor(0, 102, 204);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Question:', margin, yPosition);
      yPosition += 25;
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      addText(entry.question, 11, false, [0, 0, 0], 1.5);
      addSpacing(20);
      
      // Add Answer section
      pdf.setTextColor(0, 102, 204);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Answer:', margin, yPosition);
      yPosition += 25;
      
      // Process answer with markdown-like formatting
      pdf.setTextColor(0, 0, 0);
      const answerLines = entry.answer.split('\n');
      
      for (let i = 0; i < answerLines.length; i++) {
        let line = answerLines[i];
        
        // Skip empty lines but add spacing
        if (!line.trim()) {
          addSpacing(8);
          continue;
        }
        
        // Handle headers
        if (line.startsWith('####')) {
          addSpacing(9);
          addText(line.replace(/^####\s*/, ''), 12, true, [0, 102, 204], 1.3);
          addSpacing(8);
        } else if (line.startsWith('###')) {
          addSpacing(10);
          addText(line.replace(/^###\s*/, ''), 13, true, [0, 102, 204], 1.3);
          addSpacing(8);
        } else if (line.startsWith('##')) {
          addSpacing(12);
          addText(line.replace(/^##\s*/, ''), 14, true, [0, 102, 204], 1.3);
          addSpacing(8);
        } else if (line.startsWith('#')) {
          addSpacing(15);
          addText(line.replace(/^#\s*/, ''), 16, true, [0, 102, 204], 1.3);
          addSpacing(10);
        }
        // Handle bullet points
        else if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          const bulletText = '• ' + line.trim().substring(2);
          addText(bulletText, 11, false, [0, 0, 0], 1.5);
        }
        // Handle numbered lists
        else if (/^\d+\.\s/.test(line.trim())) {
          addText(line.trim(), 11, false, [0, 0, 0], 1.5);
        }
        // Regular text
        else {
          // Simple bold handling - remove ** markers and make text bold
          if (line.includes('**')) {
            // For now, just remove the markers and treat as normal text
            // Full markdown parsing would require more complex logic
            line = line.replace(/\*\*/g, '');
          }
          addText(line, 11, false, [0, 0, 0], 1.5);
        }
      }
      
      // Download PDF
      const timestamp_str = new Date().toISOString().split('T')[0];
      pdf.save(`${categoryName.replace(/\s+/g, '_')}_${timestamp_str}.pdf`);
      
      setExportingIndex(null);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please try again.');
      setExportingIndex(null);
    }
  };

  const selectedCategoryInfo = categories.find(c => c.category_id === selectedCategory);

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3 mb-0">
              <i className="bi bi-database me-2"></i>
              Resources AI Assistant
            </h1>
            <div className="d-flex gap-2">
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowCategoryManagementModal(true)}
                >
                  <i className="bi bi-gear me-2"></i>
                  Manage Categories
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowUploadModal(true)}
                disabled={!selectedCategory}
              >
                <i className="bi bi-cloud-upload me-2"></i>
                Upload Resources
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Category Selection and Query Form */}
        <div className="col-lg-4 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-folder me-2"></i>
                Category Selection
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmitQuestion}>
                <div className="mb-3">
                  <label htmlFor="categorySelect" className="form-label">
                    Select Category
                  </label>
                  <select
                    id="categorySelect"
                    className="form-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">Choose a category...</option>
                    {categories.map((category) => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                  {selectedCategoryInfo && (
                    <div className="form-text text-muted">
                      {selectedCategoryInfo.description}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="questionInput" className="form-label">
                    Your Question
                  </label>
                  <textarea
                    id="questionInput"
                    className="form-control"
                    rows="4"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask a question about the resources in the selected category..."
                    disabled={loading}
                  />
                </div>

                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !selectedCategory || !question.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search me-2"></i>
                        Query Knowledge Base
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={clearChat}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Clear Chat
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Chat History and Response */}
        <div className="col-lg-8 d-flex flex-column" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="card flex-grow-1 d-flex flex-column" style={{ height: '100%' }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="bi bi-chat-dots me-2"></i>
                Knowledge Base Conversation
              </h5>
            </div>
            <div className="card-body chat-scroll-area flex-grow-1 d-flex flex-column" style={{ minHeight: 0, height: '100%', overflow: 'hidden' }}>
              {/* Ensure vertical scroll for overflow */}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {chatHistory.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-chat-square-dots display-4 mb-3"></i>
                  <p>No conversation yet. Select a category and ask a question to get started!</p>
                </div>
              ) : (
                <div className="chat-container">
                  {chatHistory.map((entry, index) => (
                    <div key={index} className="mb-4">
                      {/* Question */}
                      <div className="d-flex justify-content-end mb-2">
                        <div className="card bg-primary text-white" style={{ maxWidth: '80%' }}>
                          <div className="card-body py-2 px-3">
                            <small className="opacity-75">
                              {categories.find(c => c.category_id === entry.category_id)?.category_name} - 
                              {entry.timestamp ? formatUnixTimestampTime(entry.timestamp) : ''}
                            </small>
                            <div>{entry.question}</div>
                          </div>
                        </div>
                      </div>

                      {/* Answer */}
                      <div className="d-flex justify-content-start">
                        <div className="card bg-light" style={{ maxWidth: '80%' }}>
                          <div className="card-body py-2 px-3">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <small className="text-muted">
                                <i className="bi bi-robot me-1"></i>
                                Knowledge Base Assistant
                              </small>
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleCopyResponse(entry, index)}
                                  disabled={copyingIndex === index}
                                  title="Copy to clipboard"
                                >
                                  {copyingIndex === index ? (
                                    <>
                                      <i className="bi bi-check2 me-1"></i>
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-clipboard me-1"></i>
                                      Copy
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleExportResponse(entry, index)}
                                  disabled={exportingIndex === index}
                                  title="Export to PDF"
                                >
                                  {exportingIndex === index ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1"></span>
                                      Exporting...
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-file-pdf me-1"></i>
                                      Export
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                            <div className="mt-1">
                              <MarkdownRenderer content={entry.answer} />
                            </div>
                            {/* Render sources if available */}
                            {entry.sources && entry.sources.length > 0 && (
                              <div className="mt-2">
                                <CollapsibleSources sources={entry.sources} categoryId={entry.category_id} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Current Response (if loading) */}
              {loading && (
                <div className="d-flex justify-content-start">
                  <div className="card bg-light" style={{ maxWidth: '80%' }}>
                    <div className="card-body py-2 px-3">
                      <small className="text-muted">
                        <i className="bi bi-robot me-1"></i>
                        Knowledge Base Assistant
                      </small>
                      <div className="mt-1">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Searching knowledge base...
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resource Upload Modal */}
      <ResourceFileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        selectedCategory={selectedCategory}
        selectedCategoryName={selectedCategoryInfo?.category_name || 'Unknown Category'}
        categories={categories}
      />

      {/* Category Management Modal */}
      {isAdmin && (
        <CategoryManagementModal
          isOpen={showCategoryManagementModal}
          onClose={() => setShowCategoryManagementModal(false)}
          onCategoryChange={fetchCategories}
          categories={categories}
        />
      )}
    </div>
  );
};

export default Resources;